import React, { useEffect, useRef, useContext, useState } from "react";
import { BryntumGrid } from "@bryntum/schedulerpro-react";
import { CLIENTS_GRID_CONFIG } from "../../other/constants/ClientViewGridConfig.js";
import BryntumContext from "../context/bryntumContext.jsx";
import { EDITOR_KEYS } from "../../other/constants/EditorKeys.jsx";
import {
  callOnLoadOnColumns, canOpenEditor, debounce
} from "../../other/utils/otherUtils.jsx";
import useRefsInit from "../hooks/useRefsInit.jsx";
import {GRANTS} from "../../other/constants/PrivilegeTypes.jsx";
import {
  getOriginalRecord,
  swivuCheckGridGrants
} from "../../other/utils/utils.jsx";
import AuthContext from "../context/authContext.jsx";
import { processCellMenuItems } from "../../other/utils/gridCellMenuUtils.jsx";
import useEnableViewTracking from "../hooks/useEnableViewTracking.jsx";
import { VIEW_TYPES, ENTITY_TYPES } from "../../other/constants/OtherKeys.jsx";
import { STORE_KEYS } from "../../other/constants/StoreKeys.jsx";
import ViewSpecificHeader from "../components/appHeader/ViewSpecificHeader.jsx";
import { useSwivuKanban } from "../hooks/useSwivuKanban";
import { KanbanWrapper } from "@/modern/components/kanban-wrapper";
import { CopyToBoardDialog } from "@/react/components/CopyToBoardDialog";

const ClientPage = () => {
  const gridRef = useRef(null);
  const { isRefsInit, setRefs } = useRefsInit([gridRef]);
  const { user } = useContext(AuthContext);
  const context = useContext(BryntumContext);
  const { project, editorHandler, entityManager, viewManager } = context;
  useEnableViewTracking(gridRef, VIEW_TYPES.clients, isRefsInit);
  const kanban = useSwivuKanban({ gridRef, storeId: STORE_KEYS.clientStore, entityType: ENTITY_TYPES.CLIENT });
  const [bulkCopyRecords, setBulkCopyRecords] = useState(null);

  useEffect(() => {
    // Assign custom project store to the grid
    if (project) {
      gridRef.current.instance.store = project.clientsStore;
      gridRef.current.instance.store.relayAll(gridRef.current.instance, "swivuClientStore");
      // Below function is being called from setting columns (tag, status, category, team, etc)
      gridRef.current.instance.onClickSwivuEditMessage = () => {
        viewManager.openAnonymousViewByType({ viewType: VIEW_TYPES.settings, viewName: 'Settings' });
      }
      swivuCheckGridGrants(gridRef.current.instance, user);
      callOnLoadOnColumns(gridRef.current.instance.columns, project, { editorHandler, viewManager });
    }
  }, [gridRef, project, user, editorHandler, viewManager]);

  useEffect(() => {
    if (gridRef.current) {
      gridRef.current.instance.features.cellMenu.processItems = processCellMenuItems({
        entityName: "Client",
        editorHandler,
        grid: gridRef.current.instance,
        user,
        project,
        tagStoreKey: STORE_KEYS.clientTagValuesStore,
        promptOnRemove: true,
        onEditEntity(record) {
          editorHandler.openEditor(EDITOR_KEYS.clientEditorV2, getOriginalRecord(record), { client: record });
        },
        onBulkCopyTo: (selectedRecords) => setBulkCopyRecords(selectedRecords),
      });
    }
  }, [editorHandler, user]);

  const onGridCellClick = debounce(({ column, record, event }) => {
    const originalRecord = getOriginalRecord(record);

    // if (event.shiftKey) {
    //   openEntityManager({
    //     selectedBoard: context.selectedBoard,
    //     recordId: originalRecord.id,
    //     recordType: ENTITY_TYPES_KEYS.client,
    //     history: context.history,
    //     viewManager: context.viewManager
    //   });
    //   return;
    // }

    if (canOpenEditor(column, originalRecord) && event.detail === 1 && (column.isTagColumnBase || column.isTeamColumn)) {
      editorHandler.openEditor(EDITOR_KEYS.entityTagEditor, null, {
        entityRecord: originalRecord,
        event,
        column
      }, { by: "recordType", useExisting: true });
    }
  }, 300);

  return (
    <>
      <ViewSpecificHeader
        viewToggleButtonConfig={{
          isKanbanMode: kanban.isKanbanMode,
          handleOnToggleView: kanban.handleOnToggleView,
          isGroupingApplied: kanban.isGroupingApplied,
          applyGroupingAndOpenKanban: kanban.applyGroupingAndOpenKanban,
          kanbanFieldConfig: kanban.kanbanFieldConfig,
          activeConfig: kanban.activeConfig,
          getActiveGroupOrder: kanban.getActiveGroupOrder,
          openCardFieldsEditor: kanban.openCardFieldsEditor
        }}
      />
      {bulkCopyRecords && (
        <CopyToBoardDialog
          open={!!bulkCopyRecords}
          onOpenChange={(isOpen) => { if (!isOpen) setBulkCopyRecords(null); }}
          records={bulkCopyRecords}
          entityType={ENTITY_TYPES.CLIENT}
        />
      )}
      <KanbanWrapper
        {...kanban}
        onItemDblClick={(record) => {
          entityManager.openEntityManager({ entityType: ENTITY_TYPES.CLIENT, entityId: record.id });
        }}
        grid={gridRef.current?.instance}
        processCellMenuItems={gridRef.current?.instance?.features?.cellMenu?.processItems}
      />
      <BryntumGrid
        ref={setRefs(gridRef)}
        {...CLIENTS_GRID_CONFIG}
        disabled={user && !user.can(GRANTS.grant_four)}
        readOnly={user && !user.can(GRANTS.grant_four)}
        onCellClick={onGridCellClick}
        onCellDblClick={({ record, column }) => {
          const originalRecord = getOriginalRecord(record);
          if (canOpenEditor(column, originalRecord)) {
            entityManager.openEntityManager({ entityType: ENTITY_TYPES.CLIENT, entityId: originalRecord.id });
          }
        }}
        onHeaderMenuItem={({ source, item }) => {
          if (item._menuId === "client") {
            const newRecord = source.store.createRecord();
            editorHandler.openEditor(EDITOR_KEYS.clientEditorV2, newRecord, { client: newRecord });
          }
        }}
        searchFeature={true}
      />
    </>
  );
};

export default ClientPage;
